import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Clock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ReminderResult {
  success: boolean;
  remindersSent: number;
  errors: number;
  details?: {
    totalEntries: number;
    successfulReminders: number;
    failedReminders: number;
    errorDetails?: string[];
  };
}

const WaitlistReminders = () => {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<ReminderResult | null>(null);

  const sendReminders = async () => {
    setSending(true);
    try {
      console.log('🚀 Triggering waitlist reminders...');
      
      const { data, error } = await supabase.functions.invoke('send-waitlist-reminders', {
        body: {}
      });

      if (error) {
        console.error('❌ Error invoking function:', error);
        throw new Error(error.message);
      }

      console.log('✅ Reminder function response:', data);
      setLastResult(data);

      if (data.success) {
        toast.success(`Reminders sent! ${data.remindersSent} emails delivered.`);
      } else {
        toast.error('Failed to send reminders. Check the logs.');
      }

    } catch (error: any) {
      console.error('💥 Error sending reminders:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Mail className="h-5 w-5 text-blue-600" />
          <span>Waitlist Reminders</span>
        </CardTitle>
        <CardDescription>
          Send reminder emails to unconfirmed waitlist signups (older than 24 hours)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Targets users who signed up more than 24 hours ago</span>
          </div>
          <Button 
            onClick={sendReminders} 
            disabled={sending}
            className="flex items-center space-x-2"
          >
            {sending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            <span>{sending ? 'Sending...' : 'Send Reminders'}</span>
          </Button>
        </div>

        {lastResult && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              {lastResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <span className="font-medium">
                {lastResult.success ? 'Reminders Sent Successfully' : 'Error Sending Reminders'}
              </span>
            </div>
            
            {lastResult.details && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <div>📧 Total unconfirmed entries: {lastResult.details.totalEntries}</div>
                <div>✅ Successful reminders: {lastResult.details.successfulReminders}</div>
                {lastResult.details.failedReminders > 0 && (
                  <div>❌ Failed reminders: {lastResult.details.failedReminders}</div>
                )}
                {lastResult.details.errorDetails && lastResult.details.errorDetails.length > 0 && (
                  <div className="mt-2">
                    <div className="font-medium text-red-600">Errors:</div>
                    <ul className="list-disc list-inside">
                      {lastResult.details.errorDetails.map((error, index) => (
                        <li key={index} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p>💡 <strong>Tip:</strong> This will only send to users who haven't confirmed their email and signed up more than 24 hours ago.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WaitlistReminders;